import { useQuery } from '@tanstack/react-query';
import type { ValidityRule } from 'fuel-benchmark-contracts';
import { Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { StatusText } from '../../components/ui/StatusText';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { catalogueStatusTone, describeRuleDefinition, severityTone } from './condition-display';
import { ValidityRuleSheet } from './ValidityRuleSheet';

const PAGE_SIZE = 200;

/**
 * The rules every submitted run is judged against. Readable by anyone signed in — an engineer has to be able to
 * see why a run failed — and editable by managers, which is how the business-side protocol track replaces the
 * placeholder numbers without a code change (plan §11).
 */
export const ValidityRulesTab = () => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();
  const isManager = user?.role === 'MANAGER';

  const [editing, setEditing] = useState<ValidityRule | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const rules = useQuery(
    orpc.config.validityRules.list.queryOptions({ input: { page: 1, pageSize: PAGE_SIZE, sortBy: 'ruleKey', sortDirection: 'asc' } }),
  );

  const columns: DataTableColumns<ValidityRule> = useMemo(
    () => [
      {
        accessorKey: 'ruleKey',
        header: t('conditions.rules.columns.rule'),
        cell: ({ row }) => <span className="font-mono text-xs text-foreground">{row.original.ruleKey}</span>,
      },
      {
        id: 'protocol',
        accessorFn: (rule) => rule.protocolCode,
        header: t('conditions.rules.columns.protocol'),
        cell: ({ row }) => (
          <InfoCell
            title={`${row.original.protocolCode} v${row.original.protocolVersion}`}
            subline={row.original.definition.field}
          />
        ),
      },
      {
        id: 'definition',
        accessorFn: (rule) => describeRuleDefinition(rule.definition),
        header: t('conditions.rules.columns.definition'),
        cell: ({ row }) => <span className="text-foreground">{describeRuleDefinition(row.original.definition)}</span>,
      },
      {
        accessorKey: 'severity',
        header: t('conditions.rules.columns.severity'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.severity.${row.original.severity}`)}
            tone={severityTone(row.original.severity)}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: t('conditions.rules.columns.status'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.status.${row.original.status}`)}
            tone={catalogueStatusTone(row.original.status)}
          />
        ),
      },
      {
        id: 'rowActions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          isManager ? (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('conditions.rules.edit')}
                onClick={() => {
                  setEditing(row.original);
                  setIsSheetOpen(true);
                }}
              >
                <Pencil className="size-3.5" />
              </Button>
            </div>
          ) : null,
      },
    ],
    [isManager, t],
  );

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        {t('conditions.rules.subtitle')} {isManager ? '' : t('conditions.rules.readOnly')}
      </p>

      {rules.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {t('errors.loadFailed')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={rules.data?.items ?? []}
        isLoading={rules.isPending}
        emptyMessage={t('conditions.rules.empty')}
      />

      <ValidityRuleSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        rule={editing}
      />
    </div>
  );
};
